import React, {useState} from 'react';
import {useDispatch} from 'react-redux';
import styled from 'styled-components';
import {isEmpty} from 'lodash';
import {numberFormattedText, stringFormat} from '@ey/utils';
import Modal from '../../../../Modal/Modal';
import FormField from '../../../../BaseComponentWrappers/MotifFormField';
import ErrorMessage from '../../../../BaseComponentWrappers/MotifErrorMessage';
import RichTextEditorWrapper from '../../../../KendoEditor/RichTextEditorWrapper';
import LoadingIndicator from '../../../../LoadingIndicator/LoadingIndicator';
import ErrorSummary from '../../../../ErrorSummary/ErrorSummary';
import Utility from '../../../../../../util/utilityfunctions';
import {maxLengthAttributeDescription} from '../../../../../../util/uiconstants';
import {
	addSampleAttributeStatus,
	removeAttributeFromSampleItem,
	updateSampleAttributeStatus
} from '../../../../../../actions/SampleItems/sampleItemActions';
import {currentResource} from '../../../../../../util/utils';
import {useDocumentId, useEngagementId, useUserPreferences} from '../../../../../../util/customHooks';

export default function CreateEditAdditionalInfoModal({
	isCreate = false,
	isEdit = false,
	showModal,
	setShowModal,
	isRemove = false,
	sampleDataItem,
	sampleItemId,
	sampleListId,
	selectedAttributeStatusId,
	onHideModal,
	isRemoveAttributesRequired,
	modalTitle = ''
}) {
	const engagementId = useEngagementId();
	const documentId = useDocumentId();
	const userPreferences = useUserPreferences();
	const dispatch = useDispatch();
	const [inputAddtionalDescription, setInputAddtionalDescription] = useState(isEdit ? sampleDataItem.rationale : '');
	const [isLoading, setIsLoading] = useState(false);
	const additionDocumentationLabels = currentResource.AdditionDocumentationLabels;
	const modalDescription = isCreate
		? additionDocumentationLabels.enterAdditionalDocTitle
		: additionDocumentationLabels.editAdditionalDocDesc;
	const [hasValidation, setHasValidation] = useState(false);
	const [hasDescriptionMaxLengthError, sethasDescriptionMaxLengthError] = useState(false);
	const [charCount, setCharCount] = useState(0);

	const onHide = () => {
		cleanUp();
		if (showModal) setShowModal(!showModal);
		onHideModal();
	};

	const cleanUp = () => {
		!isRemove && setInputAddtionalDescription('');
		setHasValidation(false);
		setIsLoading(false);
		sethasDescriptionMaxLengthError(false);
	};

	const validation = () => {
		return (
			inputAddtionalDescription !== '' &&
			inputAddtionalDescription.trim() !== '' &&
			charCount <= maxLengthAttributeDescription
		);
	};

	const onOkClick = () => {
		if (validation()) {
			setIsLoading(true);
			if (isRemoveAttributesRequired) {
				removeAttributeStatus(sampleDataItem.attributeId);
			} else {
				isCreate ? addAttributeStatus() : editAttributeStatus();
			}
		} else {
			setHasValidation(true);
		}
	};

	const addAttributeStatus = () => {
		const postData = {
			attributeId: sampleDataItem.attributeId,
			attributeStatusId: selectedAttributeStatusId,
			rationale: inputAddtionalDescription,
			hostDocumentId: documentId
		};
		return dispatch(addSampleAttributeStatus(engagementId, sampleListId, sampleItemId, postData))
			.then(() => {
				setShowModal(false);
			})
			.finally(() => {
				cleanUp();
			});
	};

	const editAttributeStatus = () => {
		const payload = {
			attributeStatusId: selectedAttributeStatusId,
			rationale: inputAddtionalDescription,
			documentId: documentId
		};
		return dispatch(
			updateSampleAttributeStatus(engagementId, sampleListId, sampleItemId, sampleDataItem.attributeId, payload)
		)
			.then(() => {
				setShowModal(false);
			})
			.finally(() => {
				cleanUp();
			});
	};

	const removeAttributeStatus = (attributeId) => {
		const deleteAttributeData = {
			engagementId: engagementId,
			sampleListId: sampleListId,
			sampleItemId: sampleItemId,
			attributeId: attributeId,
			hostDocumentId: documentId
		};
		dispatch(removeAttributeFromSampleItem(deleteAttributeData))
			.then(() => {
				const postData = {
					attributeId: sampleDataItem.attributeId,
					attributeStatusId: selectedAttributeStatusId,
					rationale: inputAddtionalDescription,
					hostDocumentId: documentId
				};
				dispatch(addSampleAttributeStatus(engagementId, sampleListId, sampleItemId, postData));
			})
			.finally(() => {
				setShowModal(false);
				cleanUp();
				setIsLoading(false);
			});
	};

	const getInnerHtmlFromRichText = (html) => {
		let element = document.createElement('div');
		element.innerHTML = html;
		return element.innerHTML;
	};

	const handleCharCount = (count) => {
		setCharCount(count);
	};

	const handleOnChange = (text) => {
		let additionalDescription = Utility.getInnerTextFromRichText(text.trim());
		const additionalDescriptionInnerHtml = getInnerHtmlFromRichText(text);
		if (isEmpty(additionalDescription)) {
			setInputAddtionalDescription('');
		} else {
			setInputAddtionalDescription(text.trim());
		}
		if (additionalDescriptionInnerHtml?.length > maxLengthAttributeDescription) {
			setHasValidation(true);
			sethasDescriptionMaxLengthError(true);
		} else {
			sethasDescriptionMaxLengthError(false);
		}
	};

	const renderErrorMsg = (msg) => {
		return (
			<ErrorMessage
				message={Utility.createRichTextElement(
					stringFormat(
						msg,
						numberFormattedText(userPreferences.numberFormat, charCount.toString()),
						numberFormattedText(userPreferences.numberFormat, maxLengthAttributeDescription)
					)
				)}
			/>
		);
	};

	const renderDocModal = () => {
		return (
			<StyledAdditionDocumentationBodyWrapper>
				{isLoading && (
					<section className="loaderView">
						<LoadingIndicator show view="inlineView" />
					</section>
				)}
				{!isLoading && (
					<>
						<section className="modal-description">
							{Utility.createRichTextElement(
								stringFormat(
									modalDescription,
									!isRemove ? additionDocumentationLabels.save : additionDocumentationLabels.remove
								)
							)}
						</section>
						<section className="separator"></section>

						{isEdit && (
							<section className="attribute-index motif-body2-default-light">
								{additionDocumentationLabels.attributeIndexLabel} :
								<span className="motif-body2-default-regular">{sampleDataItem.attributeIndex}</span>
							</section>
						)}
						<FormField className="additional-description-input">
							<>
								<RichTextEditorWrapper
									uniqueId="additionalRichTextEditorWrappers"
									label={additionDocumentationLabels.additionalDocTitle}
									charactersLabel={additionDocumentationLabels.characters}
									ofLabel={additionDocumentationLabels.of}
									onChange={(e) => {
										handleOnChange(e);
									}}
									maxCharCount={maxLengthAttributeDescription}
									showMaxCharacterCount
									placeholder={additionDocumentationLabels.additionalDocTitlePlaceholder}
									useMinimalToolset
									value={inputAddtionalDescription}
									theme={Utility.getTheme()}
									numberFormat={userPreferences.numberFormat}
									charCountChanged={handleCharCount}
								/>

								<section className="errorMsg">
									{hasValidation &&
										inputAddtionalDescription.trim() === '' &&
										renderErrorMsg(additionDocumentationLabels.required)}
									{hasValidation &&
										hasDescriptionMaxLengthError &&
										renderErrorMsg(additionDocumentationLabels.descriptionMaxLengthError)}
								</section>
							</>
						</FormField>
					</>
				)}
			</StyledAdditionDocumentationBodyWrapper>
		);
	};

	return (
		<>
			{showModal && (
				<Modal
					show={showModal}
					modalsize={'medium'}
					title={isRemove ? additionDocumentationLabels.removeAdditionalDocumentation : modalTitle}
					showButtons
					modalbodystyle={'customScrollbar'}
					confirmBtnTitle={isRemove ? additionDocumentationLabels.remove : additionDocumentationLabels.save}
					closeTitle={additionDocumentationLabels.cancel}
					closeBtnTitle={additionDocumentationLabels.cancel}
					onOkClick={() => onOkClick()}
					onHide={onHide}
					isOkButtonDisabled={isLoading}
					isSaveCreateButtonDisabled={isLoading}
					isCancelButtonDisabled={isLoading}
					disableClose={isLoading}
				>
					<section>{renderDocModal()}</section>
				</Modal>
			)}
		</>
	);
}

const StyledAdditionDocumentationBodyWrapper = styled.section`
	.separator {
		border-top: var(--px-1) solid var(--neutrals-100);
		margin-top: var(--px-24);
		margin-bottom: var(--px-24);
	}
	.attribute-index {
		display: flex;
		gap: var(--px-4);
		margin-bottom: var(--px-24);
	}
	.errorMsg {
		height: var(--px-24);
		line-height: var(--px-18);
	}
	.loaderView {
		padding-top: calc(var(--px-70) - var(--spacing-large));
		width: calc(100% - calc(2 * var(--spacing-x-large)));
		display: flex;
		justify-content: center;
	}

	.additional-description-input {
		.ql-editor {
			height: var(--px-200);
			li[data-list='bullet'] > .ql-ui:before {
				font-family: auto;
			}
		}
		label {
			color: var(--neutrals-700);
			font-weight: 400;
			font-size: var(--px-14);
			line-height: var(--px-20);
		}
	}
	.modal-description {
		line-height: var(--px-24);
		font-weight: 300;
		font-size: var(--px-16);
		color: var(--neutrals-900);
	}
`;
